import { useCallback, useEffect, useState } from 'react';
import { normalizeCode } from '@shared/protocol';
import { Home, Rules, type HomeChoice } from './screens/Home';
import { BotGame } from './local/BotGame';
import { PassPlay } from './local/PassPlay';
import { RemoteGame } from './remote/RemoteGame';
import { AnnounceProvider } from './ui/shell';

type Route =
  | { kind: 'home' }
  | { kind: 'rules' }
  | { kind: 'bots'; name: string; bots: number }
  | { kind: 'passPlay'; names: string[] }
  | { kind: 'remote'; code: string | null };

/** `#/ABCD` is the share link. Anything else is the front door. */
function routeFromHash(): Route {
  const code = normalizeCode(location.hash.replace(/^#\/?/, ''));
  return code.length >= 4 ? { kind: 'remote', code } : { kind: 'home' };
}

export function App() {
  const [route, setRoute] = useState<Route>(routeFromHash);
  // Bumped when someone starts a fresh room from a dead one: the room client
  // holds a socket and a seat, so it has to be rebuilt rather than reused.
  const [roomEpoch, setRoomEpoch] = useState(0);

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const goHome = useCallback(() => {
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    setRoute({ kind: 'home' });
  }, []);

  const choose = useCallback((choice: HomeChoice) => {
    switch (choice.kind) {
      case 'bots':
        setRoute({ kind: 'bots', name: choice.name, bots: choice.bots });
        break;
      case 'passPlay':
        setRoute({ kind: 'passPlay', names: choice.names });
        break;
      case 'host':
        setRoute({ kind: 'remote', code: null });
        break;
      case 'join':
        setRoute({ kind: 'remote', code: choice.code });
        break;
      case 'rules':
        setRoute({ kind: 'rules' });
        break;
    }
  }, []);

  return (
    <AnnounceProvider>
      <div className="app">
        {route.kind === 'home' && <Home onChoose={choose} />}
        {route.kind === 'rules' && <Rules onBack={goHome} />}
        {route.kind === 'bots' && <BotGame name={route.name} bots={route.bots} onExit={goHome} />}
        {route.kind === 'passPlay' && <PassPlay names={route.names} onExit={goHome} />}
        {route.kind === 'remote' && (
          <RemoteGame
            key={`${route.code ?? 'new'}:${roomEpoch}`}
            joinCode={route.code}
            onExit={goHome}
            onNewRoom={() => {
              if (location.hash) history.replaceState(null, '', location.pathname + location.search);
              setRoute({ kind: 'remote', code: null });
              setRoomEpoch((n) => n + 1);
            }}
          />
        )}
      </div>
    </AnnounceProvider>
  );
}
